import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { LogoutConfirmationModal } from '../components/LogoutConfirmationModal';
import {
  uploadAvatar,
  removeAvatar,
  updateUserProfile,
  uploadAssignmentResource,
  type AssignmentResource,
  fetchAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  type AssignmentDto,
  fetchAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  type AnnouncementDto,
  fetchTimetables,
  type TimetableDto,
  fetchTeacherSubjects,
  fetchSubjects,
  type TeacherSubjectDto,
  type SubjectDto,
  fetchActiveClasses,
  type SchoolClassDto,
  fetchClassAnnouncements,
  fetchAssignedClasses,
  createClassAnnouncement,
  deleteClassAnnouncement,
  type ClassAnnouncementDto,
  type AssignedClassDto,
  fetchActiveClubs,
  fetchClubMembers,
  addClubMember,
  removeClubMember,
  updateClubMemberRole,
  type ClubDto,
  type ClubMemberDto
} from '../api/axios';
import { TeacherGradingSection } from '../components/grading';
import { DashboardView } from '../components/teacher/DashboardView';
import { ClassAnnouncementModal } from '../components/ClassAnnouncementModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import styles from './TeacherDashboard.module.css';
import { classLevelWord, formatClassLevelLabel } from '../utils/classLevelLabel';
import {
  resolveTeacherSubjectLabel,
  teacherMatchesSubjectForm,
} from '../utils/teacherSubjectDisplay';

type Section = 'dashboard' | 'assignments' | 'grading' | 'timetable' | 'announcements' | 'clubs' | 'profile';
type AssignmentsView = 'main' | 'create' | 'detail' | 'edit';

interface TeacherAssignment {
  id: number;
  className: string;
  classLevel?: number | null;
  subject: string;
  title: string;
  dueDate: string; // ISO string (YYYY-MM-DD) preferred
  status: 'pending' | 'completed';
  description?: string;
  resources?: AssignmentResource[];
  teacherName?: string;
  totalMarks?: number | null;
}

// GradingClass interface removed - grading is now handled in TeacherGradingSection

export const TeacherDashboard: React.FC = () => {
  const { user, logout, setUser } = useAuth();
  const { currentSchool, userSchool, getSchoolInfo, isSwitchingSchool, schoolSwitchId, completeSchoolSwitch } = useSchool();
  const teacherSchool = userSchool ?? currentSchool;
  const { isMobileMenuOpen, isMobileView, toggleMobileMenu, closeMobileMenu } = useMobileMenu();
  const schoolInfo = getSchoolInfo(currentSchool);
  // Get full display name for profile areas
  const teacherName = user?.name || (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.first_name) || 'Teacher';

  const [activeSection, setActiveSection] = useState<Section>('dashboard');
  const [assignmentsView, setAssignmentsView] = useState<AssignmentsView>('main');
  const [selectedAssignment, setSelectedAssignment] = useState<TeacherAssignment | null>(null);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatar_url ?? null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<AssignmentResource[]>([]);
  const [docUploading, setDocUploading] = useState(false);
  const docInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [phoneNumber, setPhoneNumber] = useState<string>(user?.phone_number ?? '');
  const [recoveryEmail, setRecoveryEmail] = useState<string>((user as any)?.recovery_email ?? '');
  const [savingProfile, setSavingProfile] = useState(false);

  // Club management (teacher view)
  const [teacherClubs, setTeacherClubs] = useState<ClubDto[]>([]);
  const [selectedClubForMembers, setSelectedClubForMembers] = useState<ClubDto | null>(null);
  const [clubMembers, setClubMembers] = useState<ClubMemberDto[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [loadingClubMembers, setLoadingClubMembers] = useState(false);
  const [selectedStudentIdForClub, setSelectedStudentIdForClub] = useState<number | ''>('');

  const [newSubject, setNewSubject] = useState('');
  const [newForm, setNewForm] = useState<number | ''>('');
  const [newTopic, setNewTopic] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newTotalMarks, setNewTotalMarks] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [/*assignmentsLoading*/, setAssignmentsLoading] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(true);
  const [toGradeExpanded, setToGradeExpanded] = useState(true);
  const [expandedForms, setExpandedForms] = useState<Record<string, boolean>>({});
  const [announcements, setAnnouncements] = useState<AnnouncementDto[]>([]);
  const [clubAnnouncements, setClubAnnouncements] = useState<AnnouncementDto[]>([]);
  const [availableAnnouncementClubs, setAvailableAnnouncementClubs] = useState<ClubDto[]>([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [loadingClubAnnouncements, setLoadingClubAnnouncements] = useState(false);
  const [timetables, setTimetables] = useState<TimetableDto[]>([]);
  const [loadingTimetables, setLoadingTimetables] = useState(false);
  const [teacherSubjects, setTeacherSubjects] = useState<TeacherSubjectDto[]>([]);
  const [allSubjects, setAllSubjects] = useState<SubjectDto[]>([]);
  const [schoolClasses, setSchoolClasses] = useState<SchoolClassDto[]>([]);
  
  // Class announcements (teacher-specific)
  const [classAnnouncements, setClassAnnouncements] = useState<ClassAnnouncementDto[]>([]);
  const [loadingClassAnnouncements, setLoadingClassAnnouncements] = useState(false);
  const [assignedClasses, setAssignedClasses] = useState<AssignedClassDto[]>([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showClubAnnouncementModal, setShowClubAnnouncementModal] = useState(false);
  const [editingClubAnnouncement, setEditingClubAnnouncement] = useState<AnnouncementDto | null>(null);
  const [clubAnnouncementForm, setClubAnnouncementForm] = useState({
    title: '',
    message: '',
    clubId: 0,
    sendEmail: false,
  });
  const announcementLastSeenKey = user?.id ? `announcementLastSeen:teacher:${user.id}` : null;

  const getLastSeenAnnouncementTime = (): number => {
    if (!announcementLastSeenKey) return 0;
    const raw = window.localStorage.getItem(announcementLastSeenKey);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const markAnnouncementsAsSeen = () => {
    if (!announcementLastSeenKey) return;
    window.localStorage.setItem(announcementLastSeenKey, String(Date.now()));
  };

  const countNewAnnouncements = (items: Array<{ created_at: string }>): number => {
    const lastSeen = getLastSeenAnnouncementTime();
    if (lastSeen <= 0) return 0;
    return items.reduce((count, item) => {
      const createdAt = new Date(item.created_at).getTime();
      return Number.isFinite(createdAt) && createdAt > lastSeen ? count + 1 : count;
    }, 0);
  };
  const newAnnouncementCount =
    countNewAnnouncements(announcements) +
    countNewAnnouncements(classAnnouncements) +
    countNewAnnouncements(clubAnnouncements);
  const bellBadgeCount = newAnnouncementCount > 99 ? '99+' : String(newAnnouncementCount);
  const normalizedSubjectOptions = useMemo(() => {
    const groupedByForm: Record<number, Array<{ key: string; value: string; label: string }>> = {};

    for (const ts of teacherSubjects) {
      const form = ts.form;
      const label = resolveTeacherSubjectLabel(ts, allSubjects);
      if (!label) continue;
      const normalizedName = label.toLowerCase();
      if (!groupedByForm[form]) groupedByForm[form] = [];
      if (groupedByForm[form].some((subjectOption) => subjectOption.key === normalizedName)) continue;

      groupedByForm[form].push({
        key: normalizedName,
        value: label.toUpperCase(),
        label,
      });
    }

    return groupedByForm;
  }, [teacherSubjects, allSubjects]);
  const renderLearnMore = (label: string, items: string[]) => (
    <div className={styles.learnMoreTrigger}>
      <button type="button" aria-label={label} className={styles.learnMoreButton}>?</button>
      <div className={styles.learnMoreTooltip}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
          {items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </div>
  );

  const seenSchoolFetchRef = useRef(false);
  const schoolSwitchTimeoutRef = useRef<number | null>(null);

  const anySchoolLoading =
    loadingAnnouncements ||
    loadingClassAnnouncements ||
    loadingTimetables ||
    loadingClubs ||
    loadingClubMembers;

  useEffect(() => {
    if (!isSwitchingSchool) {
      seenSchoolFetchRef.current = false;
      if (schoolSwitchTimeoutRef.current !== null) {
        window.clearTimeout(schoolSwitchTimeoutRef.current);
        schoolSwitchTimeoutRef.current = null;
      }
      return;
    }

    if (anySchoolLoading) {
      seenSchoolFetchRef.current = true;
    }

    if (seenSchoolFetchRef.current && !anySchoolLoading) {
      completeSchoolSwitch(schoolSwitchId);
      return;
    }

    if (schoolSwitchTimeoutRef.current === null) {
      schoolSwitchTimeoutRef.current = window.setTimeout(() => {
        completeSchoolSwitch(schoolSwitchId);
        schoolSwitchTimeoutRef.current = null;
      }, 6000);
    }
  }, [isSwitchingSchool, schoolSwitchId, anySchoolLoading, completeSchoolSwitch]);

  // Helper function to get the class display name based on level
  const getClassDisplayName = (level: number | ''): string => {
    if (level === '' || level === 0) return '';
    
    // Find the class from loaded school classes
    const matchingClass = schoolClasses.find(c => c.level === level);
    if (matchingClass) {
      return matchingClass.name;
    }
    
    return formatClassLevelLabel(currentSchool, level);
  };

  const inferClassLevel = (className?: string | null): number | null => {
    const normalizedClassName = className?.trim();
    if (!normalizedClassName) return null;

    const classByName = schoolClasses.find(
      (schoolClass) => schoolClass.name.trim().toLowerCase() === normalizedClassName.toLowerCase()
    );
    if (classByName?.level && classByName.level > 0) {
      return classByName.level;
    }

    const levelMatch =
      normalizedClassName.match(/\bForm\s*(\d+)\b/i) ||
      normalizedClassName.match(/\b(?:S|P|Class)\s*(\d+)\b/i) ||
      normalizedClassName.match(/\b(\d+)\b/);

    if (!levelMatch) return null;
    const parsedLevel = Number.parseInt(levelMatch[1], 10);
    return Number.isFinite(parsedLevel) && parsedLevel > 0 ? parsedLevel : null;
  };

  const formatDueDate = (value: string) => {
    if (!value) return '';
    const [datePart] = value.split('T');
    return datePart;
  };

  const getTodayDateString = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Grading classes generation removed - now handled in TeacherGradingSection

  useEffect(() => {
    // keep preview in sync when user is loaded/refreshed
    // But preserve pending avatar file preview - don't override it when just updating profile
    if (!pendingAvatarFile) {
      if (user?.avatar_url) {
        setAvatarPreview(user.avatar_url);
      } else {
        setAvatarPreview(null);
      }
    }
    // Sync phone number
    if (user?.phone_number !== undefined) {
      setPhoneNumber(user.phone_number || '');
    }
    // Sync recovery email
    if ((user as any)?.recovery_email !== undefined) {
      setRecoveryEmail((user as any).recovery_email || '');
    }
  }, [user?.id, user?.avatar_url, user?.phone_number, (user as any)?.recovery_email, pendingAvatarFile]);

  // Separate effect to clear pending avatar file only on user ID change (logout/login)
  useEffect(() => {
    setPendingAvatarFile(null);
  }, [user?.id]);

  const loadAssignments = async () => {
    try {
      setAssignmentsLoading(true);
      const data = await fetchAssignments({ school: currentSchool });
      const mapped: TeacherAssignment[] = data.map((a: AssignmentDto) => ({
        id: a.id,
        className: a.class_name || 'Unassigned Class',
        classLevel: inferClassLevel(a.class_name),
        subject: a.subject,
        title: a.title,
        dueDate: a.due_date,
        status: 'pending',
        description: a.description || undefined,
        resources: (a.resources || undefined) as AssignmentResource[] | undefined,
        teacherName: a.teacher_name,
        totalMarks: a.total_marks || null,
      }));
      setAssignments(mapped);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load assignments', err);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const loadAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const data = await fetchAnnouncements({ school: currentSchool });
      // Filter announcements for teachers (target_group = 'teacher' or 'all')
      const teacherAnnouncements = data.filter(
        (a) => a.target_group === 'teacher' || a.target_group === 'all'
      );
      setAnnouncements(teacherAnnouncements);
    } catch (error) {
      console.error('Failed to load announcements', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  const loadClassAnnouncements = async () => {
    setLoadingClassAnnouncements(true);
    try {
      const data = await fetchClassAnnouncements({ school: currentSchool });
      setClassAnnouncements(data);
    } catch (error) {
      console.error('Failed to load class announcements', error);
    } finally {
      setLoadingClassAnnouncements(false);
    }
  };

  const loadClubAnnouncements = async () => {
    setLoadingClubAnnouncements(true);
    try {
      const data = await fetchAnnouncements({ type: 'club', school: currentSchool });
      setClubAnnouncements(data);
    } catch (error) {
      console.error('Failed to load club announcements', error);
    } finally {
      setLoadingClubAnnouncements(false);
    }
  };

  const loadAvailableAnnouncementClubs = async () => {
    try {
      const clubs = await fetchActiveClubs({ school: currentSchool });
      setAvailableAnnouncementClubs(clubs);
      setClubAnnouncementForm((prev) => ({
        ...prev,
        clubId: prev.clubId || clubs[0]?.id || 0,
      }));
    } catch (error) {
      console.error('Failed to load clubs for announcements', error);
      setAvailableAnnouncementClubs([]);
    }
  };

  const loadAssignedClasses = async () => {
    try {
      const data = await fetchAssignedClasses(currentSchool);
      setAssignedClasses(data);
    } catch (error) {
      console.error('Failed to load assigned classes', error);
    }
  };

  const loadTimetables = async () => {
    if (!user?.id) return;
    setLoadingTimetables(true);
    try {
      const data = await fetchTimetables(undefined, user.id, 'published', currentSchool);
      setTimetables(data);
    } catch (error) {
      console.error('Failed to load timetables', error);
    } finally {
      setLoadingTimetables(false);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Posted just now';
    if (diffMins < 60) return `Posted ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `Posted ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Posted 1 day ago';
    if (diffDays < 30) return `Posted ${diffDays} days ago`;
    return `Posted on ${date.toLocaleDateString()}`;
  };

  const getDayName = (dayIndex: number): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dayIndex];
  };

  const getTodayAndTomorrow = () => {
    const today = new Date();
    const todayIndex = today.getDay();
    const tomorrowIndex = (todayIndex + 1) % 7;
    
    return {
      today: getDayName(todayIndex),
      tomorrow: getDayName(tomorrowIndex),
    };
  };

  const getTimetableForDay = (day: string) => {
    return timetables
      .filter((tt) => tt.day === day.toLowerCase())
      .sort((a, b) => a.period - b.period);
  };

  const formatTime = (timeString: string | null): string => {
    if (!timeString) return '';
    // Extract time only (HH:mm) from datetime strings, avoiding year display
    // Handle formats: "HH:mm:ss", "HH:mm", or "YYYY-MM-DDTHH:mm:ss"
    const timePart = timeString.includes('T') 
      ? timeString.split('T')[1]?.split(':').slice(0, 2).join(':') 
      : timeString.split(':').slice(0, 2).join(':');
    return timePart || '';
  };

  const loadTeacherSubjects = async () => {
    if (!user?.id) return;
    try {
      const data = await fetchTeacherSubjects(undefined, user.id, teacherSchool);
      setTeacherSubjects(data);
    } catch (error) {
      console.error('Failed to load teacher subjects', error);
    }
  };

  const loadAllSubjects = async () => {
    try {
      const data = await fetchSubjects({ school: teacherSchool });
      setAllSubjects(data);
    } catch (error) {
      console.error('Failed to load subjects', error);
    }
  };

  // Load school classes for proper class name display
  const loadSchoolClasses = async () => {
    try {
      const data = await fetchActiveClasses({ school: currentSchool });
      setSchoolClasses(data);
    } catch (error) {
      console.error('Failed to load school classes', error);
    }
  };

  useEffect(() => {
    loadAssignments();
    loadAnnouncements();
    loadClassAnnouncements();
    loadClubAnnouncements();
    loadTimetables();
    loadTeacherSubjects();
    loadAllSubjects();
    loadSchoolClasses();
    loadAssignedClasses();
    loadAvailableAnnouncementClubs();
  }, [user?.id, teacherSchool]);

  useEffect(() => {
    if (activeSection !== 'clubs' && selectedClubForMembers) {
      setSelectedClubForMembers(null);
      setClubMembers([]);
      setSelectedStudentIdForClub('');
    }
  }, [activeSection, selectedClubForMembers]);

  useEffect(() => {
    if (activeSection !== 'announcements') return;
    markAnnouncementsAsSeen();
  }, [activeSection, announcements, classAnnouncements, clubAnnouncements]);

  // Note: grading-related useEffect removed - now handled in TeacherGradingSection component

  const handleAvatarChange: React.ChangeEventHandler<HTMLInputElement> = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (2MB = 2 * 1024 * 1024 bytes)
    if (file.size > 2 * 1024 * 1024) {
      window.alert('File size must be less than 2MB');
      return;
    }
    
    // Validate file type - use standard MIME types
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    // Also check extension as fallback since some browsers may report incorrect MIME types
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (!validTypes.includes(file.type) && (!fileExtension || !validExtensions.includes(fileExtension))) {
      window.alert('File must be an image (JPEG, PNG, GIF, or WebP)');
      return;
    }
    
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);
    setPendingAvatarFile(file);
  };

  const handleAvatarSave = async () => {
    if (!pendingAvatarFile) return;
    const currentPreview = avatarPreview;
    try {
      const updatedUser = await uploadAvatar(pendingAvatarFile);
      setUser(updatedUser);
      setAvatarPreview(updatedUser.avatar_url ?? currentPreview);
      setPendingAvatarFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update avatar', err);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveAvatar = async () => {
    if (!avatarPreview) return;
    
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }
    
    try {
      const updatedUser = await removeAvatar();
      setUser(updatedUser);
      setAvatarPreview(null);
      setPendingAvatarFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove avatar', err);
      window.alert('Failed to remove profile picture. Please try again.');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    // Preserve current avatar preview before updating user
    const currentAvatarPreview = avatarPreview;
    try {
      const updatedUser = await updateUserProfile({
        phone_number: phoneNumber || undefined,
        email: recoveryEmail || undefined,
      });
      setUser(updatedUser);
      // Restore avatar preview if it was a blob URL (pending file) or keep current preview
      if (currentAvatarPreview && (currentAvatarPreview.startsWith('blob:') || pendingAvatarFile)) {
        setAvatarPreview(currentAvatarPreview);
      }
      if (updatedUser.phone_number !== undefined) {
        setPhoneNumber(updatedUser.phone_number || '');
      }
      if ((updatedUser as any).recovery_email !== undefined) {
        setRecoveryEmail((updatedUser as any).recovery_email || '');
      }
      window.alert('Profile updated successfully!');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to update profile', err);
      const errorMessage = err.response?.data?.message || 'Failed to update profile. Please try again.';
      window.alert(errorMessage);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreateClassAnnouncement = async (data: {
    title: string;
    message: string;
    form: number;
    sendEmail: boolean;
  }) => {
    try {
      const response = await createClassAnnouncement({
        title: data.title,
        message: data.message,
        form: data.form,
        school: currentSchool,
        send_email: data.sendEmail,
      });

      // Show success message
      let successMessage = 'Announcement created successfully!';
      if (data.sendEmail) {
        successMessage += `\n\nEmails sent: ${response.emails_sent}`;
        if (response.emails_failed > 0) {
          successMessage += `\nFailed: ${response.emails_failed}`;
        }
      }
      window.alert(successMessage);

      // Reload class announcements
      await loadClassAnnouncements();
    } catch (err: any) {
      console.error('Failed to create class announcement', err);
      throw err;
    }
  };

  const handleDeleteClassAnnouncement = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await deleteClassAnnouncement(id);
      await loadClassAnnouncements();
      window.alert('Announcement deleted successfully!');
    } catch (err: any) {
      console.error('Failed to delete class announcement', err);
      window.alert('Failed to delete announcement. Please try again.');
    }
  };

  const openCreateClubAnnouncementModal = () => {
    setEditingClubAnnouncement(null);
    setClubAnnouncementForm({
      title: '',
      message: '',
      clubId: availableAnnouncementClubs[0]?.id || 0,
      sendEmail: false,
    });
    setShowClubAnnouncementModal(true);
  };

  const openEditClubAnnouncementModal = (announcement: AnnouncementDto) => {
    setEditingClubAnnouncement(announcement);
    setClubAnnouncementForm({
      title: announcement.title,
      message: announcement.message,
      clubId: announcement.club_id || 0,
      sendEmail: false,
    });
    setShowClubAnnouncementModal(true);
  };

  const handleSaveClubAnnouncement = async () => {
    if (!clubAnnouncementForm.title.trim() || !clubAnnouncementForm.message.trim()) {
      window.alert('Please provide both title and message.');
      return;
    }
    if (!clubAnnouncementForm.clubId) {
      window.alert('Please select a club.');
      return;
    }

    try {
      if (editingClubAnnouncement) {
        await updateAnnouncement(editingClubAnnouncement.id, {
          title: clubAnnouncementForm.title.trim(),
          message: clubAnnouncementForm.message.trim(),
          target_group: 'clubs',
          club_id: clubAnnouncementForm.clubId,
          school: currentSchool,
        });
      } else {
        await createAnnouncement({
          title: clubAnnouncementForm.title.trim(),
          message: clubAnnouncementForm.message.trim(),
          target_group: 'clubs',
          club_id: clubAnnouncementForm.clubId,
          school: currentSchool,
          send_email: clubAnnouncementForm.sendEmail,
        });
      }
      setShowClubAnnouncementModal(false);
      setEditingClubAnnouncement(null);
      await loadClubAnnouncements();
      window.alert(editingClubAnnouncement ? 'Club announcement updated successfully!' : 'Club announcement created successfully!');
    } catch (error: any) {
      console.error('Failed to save club announcement', error);
      window.alert(error.response?.data?.error || 'Failed to save club announcement');
    }
  };

  const handleDeleteClubAnnouncement = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this club announcement?')) {
      return;
    }
    try {
      await deleteAnnouncement(id);
      await loadClubAnnouncements();
      window.alert('Club announcement deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete club announcement', error);
      window.alert(error.response?.data?.error || 'Failed to delete club announcement');
    }
  };

  const resetAssignmentForm = () => {
    setNewSubject('');
    setNewForm('');
    setNewTopic('');
    setNewDueDate('');
    setNewTotalMarks('');
    setNewDescription('');
    setUploadedDocs([]);
  };

  const handleSaveAssignment = () => {
    if (!newSubject || !newForm || !newTopic || !newDueDate) {
      // eslint-disable-next-line no-alert
      alert(`Please fill Subject, ${classLevelWord(currentSchool)}, Topic, and Due Date before saving.`);
      return;
    }

    if (newDueDate < getTodayDateString()) {
      alert('Due date cannot be in the past. Please choose today or a future date.');
      return;
    }

    (async () => {
      try {
        // Verify teacher is assigned to this subject and form
        const teacherSubject = teacherSubjects.find((ts) =>
          teacherMatchesSubjectForm(ts, Number(newForm), newSubject, allSubjects)
        );

        if (!teacherSubject) {
          alert('You are not assigned to teach this subject for this form. Please select a subject and form you are assigned to.');
          return;
        }

        const className = getClassDisplayName(newForm);

        const created = await createAssignment({
          class_name: className,
          subject: newSubject.toUpperCase(),
          title: newTopic,
          due_date: newDueDate,
          total_marks: newTotalMarks ? Number(newTotalMarks) : null,
          description: newDescription,
          resources: uploadedDocs,
          school: currentSchool,
        });

        const mapped: TeacherAssignment = {
          id: created.id,
          className: created.class_name || className,
          classLevel: inferClassLevel(created.class_name || className),
          subject: created.subject,
          title: created.title,
          dueDate: created.due_date,
          status: 'pending',
          description: created.description || undefined,
          resources: (created.resources || undefined) as AssignmentResource[] | undefined,
          teacherName: created.teacher_name,
          totalMarks: created.total_marks || null,
        };

        setAssignments((prev) => [mapped, ...prev]);
        resetAssignmentForm();
        setAssignmentsView('main');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save assignment', err);
      }
    })();
  };

  const handleCancelAssignment = () => {
    // Check if there are any unsaved changes
    const hasChanges = 
      newSubject.trim() !== '' ||
      newForm !== '' ||
      newTopic.trim() !== '' ||
      newDueDate !== '' ||
      newTotalMarks.trim() !== '' ||
      newDescription.trim() !== '' ||
      uploadedDocs.length > 0;

    if (hasChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel? All changes will be lost.'
      );
      if (!confirmed) {
        return;
      }
    }

    // Reset form and go back to appropriate view
    resetAssignmentForm();
    if (assignmentsView === 'edit') {
      // Go back to detail view (selectedAssignment is preserved)
      setAssignmentsView('detail');
    } else {
      // Go back to main assignments list
      setAssignmentsView('main');
      setSelectedAssignment(null);
    }
  };

  const handleDeleteExistingAssignment = (assignment: TeacherAssignment) => {
    (async () => {
      try {
        await deleteAssignment(assignment.id);
        setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
        setSelectedAssignment(null);
        setAssignmentsView('main');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete assignment', err);
      }
    })();
  };

  const handleEditAssignment = (assignment: TeacherAssignment) => {
    // Pre-populate form with existing assignment data
    setNewSubject(assignment.subject);
    const inferredLevel = assignment.classLevel ?? inferClassLevel(assignment.className);
    setNewForm(inferredLevel ?? '');
    setNewTopic(assignment.title);
    setNewDueDate(assignment.dueDate.split('T')[0]); // Extract YYYY-MM-DD
    setNewTotalMarks(assignment.totalMarks?.toString() || '');
    setNewDescription(assignment.description || '');
    setUploadedDocs(assignment.resources || []);
    setSelectedAssignment(assignment);
    setAssignmentsView('edit');
  };

  const handleUpdateAssignment = () => {
    if (!selectedAssignment) return;
    if (!newSubject || !newForm || !newTopic || !newDueDate) {
      alert(`Please fill all required fields (Subject, ${classLevelWord(currentSchool)}, Topic, Due Date).`);
      return;
    }

    if (newDueDate < getTodayDateString()) {
      alert('Due date cannot be in the past. Please choose today or a future date.');
      return;
    }

    (async () => {
      try {
        // Verify teacher is assigned to this subject and form
        const teacherSubject = teacherSubjects.find((ts) =>
          teacherMatchesSubjectForm(ts, Number(newForm), newSubject, allSubjects)
        );

        if (!teacherSubject) {
          alert('You are not assigned to teach this subject for this form. Please select a subject and form you are assigned to.');
          return;
        }

        const className = getClassDisplayName(newForm);

        const payload = {
          class_name: className,
          subject: newSubject,
          title: newTopic,
          due_date: newDueDate,
          total_marks: newTotalMarks ? parseInt(newTotalMarks, 10) : null,
          description: newDescription,
          resources: uploadedDocs,
          school: currentSchool,
        };

        const updated = await updateAssignment(selectedAssignment.id, payload);

        const updatedAssignment: TeacherAssignment = {
          id: updated.id,
          className: updated.class_name || className,
          classLevel: inferClassLevel(updated.class_name || className),
          subject: updated.subject,
          title: updated.title,
          dueDate: updated.due_date,
          status: 'pending',
          description: updated.description || undefined,
          resources: updated.resources || undefined,
          teacherName: updated.teacher_name,
        };

        setAssignments((prev) => prev.map((a) => (a.id === updatedAssignment.id ? updatedAssignment : a)));
        setSelectedAssignment(updatedAssignment);
        setAssignmentsView('detail');
        resetAssignmentForm();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to update assignment', err);
      }
    })();
  };

  // Helper function to get next class for teacher
  const getNextClass = () => {
    const { today } = getTodayAndTomorrow();
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Get today's classes
    const todayTimetable = getTimetableForDay(today);
    const todayClasses = todayTimetable.sort((a, b) => {
      const timeA = formatTime(a.start_time || null);
      const timeB = formatTime(b.start_time || null);
      return timeA.localeCompare(timeB);
    });

    // Find next class today
    const nextToday = todayClasses.find(t => {
      const startTime = formatTime(t.start_time || null);
      return startTime > currentTime;
    });

    if (nextToday) return { entry: nextToday, isToday: true };

    // If no more classes today, get first class tomorrow
    const { tomorrow } = getTodayAndTomorrow();
    const tomorrowTimetable = getTimetableForDay(tomorrow);
    const tomorrowClasses = tomorrowTimetable.sort((a, b) => {
      const timeA = formatTime(a.start_time || null);
      const timeB = formatTime(b.start_time || null);
      return timeA.localeCompare(timeB);
    });
    
    if (tomorrowClasses.length > 0) {
      return { entry: tomorrowClasses[0], isToday: false, day: tomorrow };
    }

    return null;
  };

  // @ts-ignore - Keeping old dashboard content as reference, now using DashboardView component
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const teacherDashboardContent = () => {
    const { today } = getTodayAndTomorrow();
    const todayTimetable = getTimetableForDay(today);
    const todayClasses = todayTimetable.sort((a, b) => {
      const timeA = formatTime(a.start_time || null);
      const timeB = formatTime(b.start_time || null);
      return timeA.localeCompare(timeB);
    });
    const nextClass = getNextClass();
    const totalClasses = teacherSubjects.length;

    return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 28, fontWeight: 600, marginBottom: 6, color: '#333', letterSpacing: '-0.3px' }}>Welcome, {teacherName}</div>
        <div style={{ fontSize: 15, color: '#666', lineHeight: 1.5 }}>Here's what's happening today</div>
      </div>

      {/* Quick Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={{ 
          background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f', 
          padding: 20, 
          borderRadius: 8, 
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>Active Assignments</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>
            {assignments.length}
          </div>
        </div>
        <div style={{ 
          background: '#059669', 
          padding: 20, 
          borderRadius: 8, 
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>Classes Assigned</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>
            {totalClasses}
          </div>
        </div>
        <div style={{ 
          background: '#2563eb', 
          padding: 20, 
          borderRadius: 8, 
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>Today's Classes</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>
            {todayClasses.length}
          </div>
        </div>
        <div style={{ 
          background: '#4b5563', 
          padding: 20, 
          borderRadius: 8, 
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>Announcements</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>
            {announcements.length}
          </div>
        </div>
      </div>

      {/* Two Column Layout: Next Class & Today's Schedule Count */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 24 }}>
        {/* Next Class Card */}
        <div style={{ 
          background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f', 
          borderRadius: 12, 
          padding: 24, 
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div style={{ fontSize: 14, opacity: 0.95, marginBottom: 12, fontWeight: 500 }}>
            📍 Next Class
          </div>
          {loadingTimetables ? (
            <div style={{ fontSize: 15, opacity: 0.9 }}>Loading...</div>
          ) : nextClass ? (
            <>
              <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                {nextClass.entry.subject?.name || 'No Subject'}
              </div>
              <div style={{ fontSize: 14, opacity: 0.95, marginBottom: 12 }}>
                {formatClassLevelLabel(currentSchool, nextClass.entry.form)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                {nextClass.entry.start_time && (
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    ⏰ {formatTime(nextClass.entry.start_time || null)}
                    {nextClass.entry.end_time && ` - ${formatTime(nextClass.entry.end_time || null)}`}
                  </div>
                )}
                {nextClass.entry.room && (
                  <div style={{ fontSize: 14, opacity: 0.95 }}>
                    🏫 Room {nextClass.entry.room}
                  </div>
                )}
                <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
                  {nextClass.isToday ? 'Today' : `Tomorrow`}
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 15, opacity: 0.9 }}>
              No upcoming classes scheduled
            </div>
          )}
        </div>

        {/* Today's Classes Count Card */}
        <div style={{ 
          background: '#2563eb', 
          borderRadius: 12, 
          padding: 24, 
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: 14, opacity: 0.95, marginBottom: 12, fontWeight: 500 }}>
            📅 Today's Schedule
          </div>
          {loadingTimetables ? (
            <div style={{ fontSize: 15, opacity: 0.9 }}>Loading...</div>
          ) : (
            <>
              <div style={{ fontSize: 48, fontWeight: 700, marginBottom: 8 }}>
                {todayClasses.length}
              </div>
              <div style={{ fontSize: 16, opacity: 0.95 }}>
                {todayClasses.length === 1 ? 'Class' : 'Classes'} Today
              </div>
              <button
                onClick={() => setActiveSection('timetable')}
                style={{
                  marginTop: 16,
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '10px 20px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                }}
              >
                View Full Schedule →
              </button>
            </>
          )}
        </div>
      </div>

      {/* Today's Schedule Preview */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#333' }}>
            Today's Classes
          </div>
          <button
            onClick={() => setActiveSection('timetable')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#5a5a5a',
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: '4px 0',
              fontWeight: 500,
            }}
          >
            View Full Timetable →
          </button>
        </div>

        {loadingTimetables ? (
          <div style={{ 
            background: '#fff', 
            borderRadius: 8, 
            padding: 40, 
            textAlign: 'center', 
            color: '#666',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            Loading timetable...
          </div>
        ) : todayClasses.length === 0 ? (
          <div style={{ 
            background: '#fff', 
            borderRadius: 8, 
            padding: 40, 
            textAlign: 'center', 
            color: '#999',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 16, marginBottom: 8, color: '#666' }}>No classes scheduled for today</div>
            <div style={{ fontSize: 13, color: '#999' }}>Enjoy your free day! Check the full timetable for other days.</div>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gap: 12,
          }}>
            {todayClasses.map((entry) => {
              const isNextClass = nextClass?.entry.id === entry.id && nextClass.isToday;
              return (
                <div
                  key={entry.id}
                  style={{
                    background: isNextClass ? '#fffbeb' : '#fff',
                    border: isNextClass ? '1.5px solid #fbbf24' : '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 18,
                    boxShadow: isNextClass 
                      ? '0 4px 12px rgba(255, 193, 7, 0.3)' 
                      : '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    if (!isNextClass) {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isNextClass) {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {isNextClass && (
                    <div style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: '#ffc107',
                      color: '#333',
                      padding: '4px 10px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      Next
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: 20, 
                        fontWeight: 700, 
                        marginBottom: 6, 
                        color: '#333' 
                      }}>
                        {entry.subject?.name || 'No Subject'}
                        {entry.subject?.code && (
                          <span style={{ fontSize: 14, fontWeight: 500, color: '#666', marginLeft: 8 }}>
                            ({entry.subject.code})
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                        👥 {formatClassLevelLabel(currentSchool, entry.form)}
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {entry.start_time && (
                          <div style={{ fontSize: 13, color: '#555' }}>
                            ⏰ {formatTime(entry.start_time || null)}
                            {entry.end_time && ` - ${formatTime(entry.end_time || null)}`}
                          </div>
                        )}
                        {entry.room && (
                          <div style={{ fontSize: 13, color: '#555' }}>
                            🏫 Room {entry.room}
                          </div>
                        )}
                        <div style={{ fontSize: 13, color: '#555' }}>
                          📋 Period {entry.period}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Announcements */}
      <div style={{ 
        fontSize: 20, 
        fontWeight: 600, 
        color: '#333', 
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <span style={{ fontSize: 22 }}>📢</span>
        <span>Announcements</span>
      </div>
      {loadingAnnouncements ? (
        <div style={{ 
          border: '1px solid #d0d0d0', 
          borderRadius: 8, 
          background: '#ffffff', 
          padding: 24, 
          marginBottom: 12, 
          textAlign: 'center', 
          color: '#666',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}>
          Loading announcements...
        </div>
      ) : announcements.length === 0 ? (
        <div style={{ 
          border: '1px solid #d0d0d0', 
          borderRadius: 8, 
          background: '#ffffff', 
          padding: 24, 
          marginBottom: 12, 
          textAlign: 'center', 
          color: '#999',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
          <div>No announcements available</div>
        </div>
      ) : (
        announcements.slice(0, 2).map((announcement) => (
          <div
            key={announcement.id}
            style={{
              border: '1px solid #d0d0d0',
              borderRadius: 8,
              background: '#ffffff',
              padding: '16px 20px',
              marginBottom: 12,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.borderColor = '#bbb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = '#d0d0d0';
            }}
            onClick={() => setActiveSection('announcements')}
          >
            <div style={{ 
              fontSize: 16, 
              fontWeight: 600, 
              color: '#333', 
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <span style={{ fontSize: 18 }}>📌</span>
              <span>{announcement.title}</span>
            </div>
            <div style={{ 
              fontSize: 12, 
              color: '#777', 
              marginBottom: 10,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span>🕒</span>
              <span>{formatTimeAgo(announcement.created_at)}</span>
            </div>
            <div style={{ 
              fontSize: 14, 
              color: '#555',
              lineHeight: 1.6,
            }}>
              {announcement.message}
            </div>
          </div>
        ))
      )}
      {announcements.length > 2 && (
        <button
          onClick={() => setActiveSection('announcements')}
          style={{
            background: '#f5f5f5',
            border: '1px solid #d0d0d0',
            borderRadius: 6,
            color: '#3e3e3e',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            padding: '10px 16px',
            marginTop: 8,
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e8e8e8';
            e.currentTarget.style.borderColor = '#bbb';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f5f5f5';
            e.currentTarget.style.borderColor = '#d0d0d0';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span>View all announcements</span>
          <span style={{ fontSize: 14 }}>→</span>
        </button>
      )}
    </>
    );
  };

  const viewAssignmentDetails = (assignment: TeacherAssignment) => {
    setSelectedAssignment(assignment);
    setAssignmentsView('detail');
  };

  const goToGradingForAssignment = (assignment: TeacherAssignment) => {
    // Navigate to grading section - grading state is managed in TeacherGradingSection
    setActiveSection('grading');
    setSelectedAssignment(assignment);
  };

  const assignmentsContent = () => {
    if (assignmentsView === 'create') {
      return (
        <>
          <div style={{ fontSize: 24, fontWeight: 600, marginBottom: 16, color: '#333' }}>ASSIGNMENTS</div>

          <div style={{ background: '#f0f0f0', borderRadius: 4, padding: 20, border: '1px solid #d5d5d5', maxWidth: 900 }}>
            <div style={{ display: 'grid', rowGap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                <label style={{ fontSize: 14, color: '#555' }}>
                  {classLevelWord(currentSchool)}
                  <select
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 10px',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                    }}
                    value={newForm}
                    onChange={(e) => {
                      setNewForm(e.target.value ? parseInt(e.target.value, 10) : '');
                      // Clear subject when form changes to avoid mismatches
                      setNewSubject('');
                    }}
                  >
                    <option value="">Select a form</option>
                    {Array.from(new Set(teacherSubjects.map((ts) => ts.form)))
                      .sort()
                      .map((form) => (
                        <option key={form} value={form}>
                          {formatClassLevelLabel(currentSchool, form)}
                        </option>
                      ))}
                  </select>
                </label>
                <label style={{ fontSize: 14, color: '#555' }}>
                  Subject
                  <select
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 10px',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                    }}
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    disabled={!newForm}
                  >
                    <option value="">{newForm ? 'Select a subject' : `Select ${currentSchool === 'TLC' ? 'standard' : 'form'} first`}</option>
                    {(newForm ? (normalizedSubjectOptions[newForm] ?? []) : [])
                      .map((subjectOption) => (
                        <option key={subjectOption.key} value={subjectOption.value}>
                          {subjectOption.label}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <label style={{ fontSize: 14, color: '#555' }}>
                Topic
                <input
                  style={{
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                  }}
                  placeholder="e.g. Trigonometry #1"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                />
              </label>
              <label style={{ fontSize: 14, color: '#555', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Due Date</span>
                <input
                  type="date"
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                  }}
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  min={getTodayDateString()}
                />
              </label>
              <label style={{ fontSize: 14, color: '#555' }}>
                Total marks
                <input
                  type="number"
                  style={{
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                  }}
                  placeholder="e.g. 100"
                  value={newTotalMarks}
                  onChange={(e) => setNewTotalMarks(e.target.value)}
                />
              </label>
              <label style={{ fontSize: 14, color: '#555' }}>
                Description
                <textarea
                  rows={6}
                  style={{
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                    resize: 'vertical',
                  }}
                  placeholder="Add assignment details for your students..."
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
              </label>
            </div>

            <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => docInputRef.current?.click()}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#204a9a',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                upload doc
              </button>
              <input
                ref={docInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,.pptx,.docx"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'pptx', 'docx'];
                  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

                  if (!allowedExtensions.includes(extension)) {
                    // eslint-disable-next-line no-alert
                    alert('File type not allowed. Allowed types: JPG, JPEG, PNG, PDF, PPTX, DOCX.');
                    e.target.value = '';
                    return;
                  }

                  setDocUploading(true);
                  try {
                    const resource = await uploadAssignmentResource(file);
                    setUploadedDocs((prev) => [resource, ...prev]);
                  } catch (err: any) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to upload assignment document', err);
                    
                    // Show actual validation errors
                    if (err.response?.data?.errors) {
                      const errors = err.response.data.errors;
                      const errorMessages = Object.values(errors).flat().join(', ');
                      // eslint-disable-next-line no-alert
                      alert(`Upload failed: ${errorMessages}`);
                      console.error('Validation errors:', errors);
                    } else if (err.response?.data?.message) {
                      // eslint-disable-next-line no-alert
                      alert(`Upload failed: ${err.response.data.message}`);
                      console.error('Error message:', err.response.data.message);
                    } else {
                      // eslint-disable-next-line no-alert
                      alert('Upload failed. Please check the console for details.');
                    }
                  } finally {
                    setDocUploading(false);
                    e.target.value = '';
                  }
                }}
              />
              {docUploading && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>Uploading...</div>
              )}
            </div>

            {uploadedDocs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>Attached documents</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
                  {uploadedDocs.map((doc) => {
                    const ext = doc.original_name.split('.').pop()?.toUpperCase() ?? 'FILE';
                    const isImage = doc.mime_type?.startsWith('image/');
                    const isPdf = doc.mime_type === 'application/pdf';
                    return (
                      <div
                        key={doc.id}
                        style={{
                          width: 280,
                          borderRadius: 4,
                          border: '1px solid #ccc',
                          background: '#fff',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setUploadedDocs((prev) => prev.filter((d) => d.id !== doc.id))}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: '28px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                          }}
                          title="Remove document"
                        >
                          ×
                        </button>
                        <div
                          style={{
                            height: 220,
                            background: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            position: 'relative',
                          }}
                          onClick={() => {
                            // Open in new tab for better viewing
                            window.open(doc.url, '_blank');
                          }}
                          title="Click to open in new tab"
                        >
                          {isImage ? (
                            <img 
                              src={doc.url} 
                              alt={doc.original_name} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                              onError={(e) => {
                                // Handle broken images - show fallback
                                const target = e.currentTarget;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `
                                    <div style="text-align: center; color: #999; width: 100%; padding: 20px;">
                                      <div style="font-size: 48px; margin-bottom: 8px;">📷</div>
                                      <div style="font-size: 12px; font-weight: 600; margin-bottom: 4px;">Image preview unavailable</div>
                                      <div style="font-size: 10px; color: #666;">Click to open</div>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : isPdf ? (
                            <div style={{ textAlign: 'center', padding: '20px', width: '100%' }}>
                              <div style={{ fontSize: 48, marginBottom: 8, color: '#d32f2f' }}>📄</div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4 }}>PDF Document</div>
                              <div style={{ fontSize: 10, color: '#666' }}>Click to open</div>
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', width: '100%', padding: '20px' }}>
                              <div style={{ fontSize: 32, fontWeight: 700, color: '#555', marginBottom: 8 }}>{ext}</div>
                              <div style={{ fontSize: 10, color: '#666' }}>Click to open</div>
                            </div>
                          )}
                        </div>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '6px 8px',
                            fontSize: 11,
                            color: '#204a9a',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                          title={doc.original_name}
                        >
                          {doc.original_name}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleSaveAssignment}
                style={{
                  minWidth: 140,
                  padding: '10px 18px',
                  background: '#3e3e3e',
                  color: '#fff',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleCancelAssignment}
                style={{
                  minWidth: 140,
                  padding: '10px 18px',
                  background: '#999',
                  color: '#fff',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      );
    }

    if (assignmentsView === 'edit' && selectedAssignment) {
      return (
        <>
          <div style={{ fontSize: 24, fontWeight: 600, marginBottom: 16, color: '#333' }}>
            <span
              style={{ cursor: 'pointer', color: '#666' }}
              onClick={() => {
                setAssignmentsView('detail');
                resetAssignmentForm();
              }}
            >
              ASSIGNMENTS
            </span>{' '}
            &gt; Edit Assignment
          </div>

          <div style={{ background: '#f5f5f5', borderRadius: 4, padding: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                <label style={{ fontSize: 14, color: '#555' }}>
                  {classLevelWord(currentSchool)}
                  <select
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 10px',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                    }}
                    value={newForm}
                    onChange={(e) => {
                      setNewForm(e.target.value ? parseInt(e.target.value, 10) : '');
                      // Clear subject when form changes to avoid mismatches
                      setNewSubject('');
                    }}
                  >
                    <option value="">Select a form</option>
                    {Array.from(new Set(teacherSubjects.map((ts) => ts.form)))
                      .sort()
                      .map((form) => (
                        <option key={form} value={form}>
                          {formatClassLevelLabel(currentSchool, form)}
                        </option>
                      ))}
                  </select>
                </label>
                <label style={{ fontSize: 14, color: '#555' }}>
                  Subject
                  <select
                    style={{
                      width: '100%',
                      marginTop: 4,
                      padding: '8px 10px',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      background: '#fff',
                    }}
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    disabled={!newForm}
                  >
                    <option value="">{newForm ? 'Select a subject' : `Select ${currentSchool === 'TLC' ? 'standard' : 'form'} first`}</option>
                    {(newForm ? (normalizedSubjectOptions[newForm] ?? []) : [])
                      .map((subjectOption) => (
                        <option key={subjectOption.key} value={subjectOption.value}>
                          {subjectOption.label}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <label style={{ fontSize: 14, color: '#555' }}>
                Topic
                <input
                  style={{
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                  }}
                  placeholder="e.g. Trigonometry #1"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                />
              </label>
              <label style={{ fontSize: 14, color: '#555', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Due Date</span>
                <input
                  type="date"
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                  }}
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  min={getTodayDateString()}
                />
              </label>
              <label style={{ fontSize: 14, color: '#555' }}>
                Total marks
                <input
                  type="number"
                  style={{
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                  }}
                  placeholder="e.g. 100"
                  value={newTotalMarks}
                  onChange={(e) => setNewTotalMarks(e.target.value)}
                />
              </label>
              <label style={{ fontSize: 14, color: '#555' }}>
                Description
                <textarea
                  rows={6}
                  style={{
                    width: '100%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#e3e3e3',
                    resize: 'vertical',
                  }}
                  placeholder="Add assignment details for your students..."
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
              </label>
            </div>

            <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 12 }}>
              <button
                type="button"
                onClick={() => docInputRef.current?.click()}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#204a9a',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                upload doc
              </button>
              <input
                ref={docInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,.pptx,.docx"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'pptx', 'docx'];
                  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

                  if (!allowedExtensions.includes(extension)) {
                    // eslint-disable-next-line no-alert
                    alert('File type not allowed. Allowed types: JPG, JPEG, PNG, PDF, PPTX, DOCX.');
                    e.target.value = '';
                    return;
                  }

                  setDocUploading(true);
                  try {
                    const resource = await uploadAssignmentResource(file);
                    setUploadedDocs((prev) => [resource, ...prev]);
                  } catch (err: any) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to upload assignment document', err);
                    
                    // Show actual validation errors
                    if (err.response?.data?.errors) {
                      const errors = err.response.data.errors;
                      const errorMessages = Object.values(errors).flat().join(', ');
                      // eslint-disable-next-line no-alert
                      alert(`Upload failed: ${errorMessages}`);
                      console.error('Validation errors:', errors);
                    } else if (err.response?.data?.message) {
                      // eslint-disable-next-line no-alert
                      alert(`Upload failed: ${err.response.data.message}`);
                      console.error('Error message:', err.response.data.message);
                    } else {
                      // eslint-disable-next-line no-alert
                      alert('Upload failed. Please check the console for details.');
                    }
                  } finally {
                    setDocUploading(false);
                    e.target.value = '';
                  }
                }}
              />
              {docUploading && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>Uploading...</div>
              )}
            </div>

            {uploadedDocs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>Attached documents</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
                  {uploadedDocs.map((doc) => {
                    const ext = doc.original_name.split('.').pop()?.toUpperCase() ?? 'FILE';
                    const isImage = doc.mime_type?.startsWith('image/');
                    const isPdf = doc.mime_type === 'application/pdf';
                    return (
                      <div
                        key={doc.id}
                        style={{
                          width: 280,
                          borderRadius: 4,
                          border: '1px solid #ccc',
                          background: '#fff',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setUploadedDocs((prev) => prev.filter((d) => d.id !== doc.id))}
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: '28px',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                          }}
                          title="Remove document"
                        >
                          ×
                        </button>
                        <div
                          style={{
                            height: 220,
                            background: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            position: 'relative',
                          }}
                          onClick={() => {
                            // Open in new tab for better viewing
                            window.open(doc.url, '_blank');
                          }}
                          title="Click to open in new tab"
                        >
                          {isImage ? (
                            <img 
                              src={doc.url} 
                              alt={doc.original_name} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                              onError={(e) => {
                                // Handle broken images - show fallback
                                const target = e.currentTarget;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `
                                    <div style="text-align: center; color: #999; width: 100%; padding: 20px;">
                                      <div style="font-size: 48px; margin-bottom: 8px;">📷</div>
                                      <div style="font-size: 12px; font-weight: 600; margin-bottom: 4px;">Image preview unavailable</div>
                                      <div style="font-size: 10px; color: #666;">Click to open</div>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : isPdf ? (
                            <div style={{ textAlign: 'center', padding: '20px', width: '100%' }}>
                              <div style={{ fontSize: 48, marginBottom: 8, color: '#d32f2f' }}>📄</div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4 }}>PDF Document</div>
                              <div style={{ fontSize: 10, color: '#666' }}>Click to open</div>
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', width: '100%', padding: '20px' }}>
                              <div style={{ fontSize: 32, fontWeight: 700, color: '#555', marginBottom: 8 }}>{ext}</div>
                              <div style={{ fontSize: 10, color: '#666' }}>Click to open</div>
                            </div>
                          )}
                        </div>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '6px 8px',
                            fontSize: 11,
                            color: '#204a9a',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                          title={doc.original_name}
                        >
                          {doc.original_name}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 16 }}>
              <button
                type="button"
                onClick={handleUpdateAssignment}
                style={{
                  minWidth: 140,
                  padding: '10px 18px',
                  background: '#3e3e3e',
                  color: '#fff',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={handleCancelAssignment}
                style={{
                  minWidth: 140,
                  padding: '10px 18px',
                  background: '#999',
                  color: '#fff',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      );
    }

    if (assignmentsView === 'detail' && selectedAssignment) {
      return (
        <>
          <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, color: '#333' }}>
            <span
              style={{ cursor: 'pointer', color: '#666' }}
              onClick={() => {
                setAssignmentsView('main');
                setSelectedAssignment(null);
              }}
            >
              ASSIGNMENTS
            </span>{' '}
            &gt; {selectedAssignment.className} - {selectedAssignment.subject}
          </div>

          <div style={{ background: '#4b4b4b', padding: 20, borderRadius: 4, color: '#fff', marginBottom: 16 }}>
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
              {selectedAssignment.className}: {selectedAssignment.subject}
            </div>
            <div style={{ fontSize: 16, marginBottom: 4 }}>{selectedAssignment.title}</div>
            <div style={{ fontSize: 14, marginBottom: 2 }}>DUE DATE: {formatDueDate(selectedAssignment.dueDate)}</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>Teacher: {selectedAssignment.teacherName || teacherName}</div>
          </div>

          <div style={{ background: '#f5f5f5', padding: 20, borderRadius: 4 }}>
            <h3 style={{ marginTop: 0, marginBottom: 10 }}>Assignment details</h3>
            <p style={{ fontSize: 14, color: '#444', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              {selectedAssignment.description || 'No description provided for this assignment.'}
            </p>

            {selectedAssignment.resources && selectedAssignment.resources.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ marginBottom: 8 }}>Attached documents</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'flex-start' }}>
                  {selectedAssignment.resources.map((doc) => {
                    const ext = doc.original_name.split('.').pop()?.toUpperCase() ?? 'FILE';
                    const isImage = doc.mime_type?.startsWith('image/');
                    const isPdf = doc.mime_type === 'application/pdf';
                    return (
                      <div
                        key={doc.id}
                        style={{
                          width: 280,
                          borderRadius: 4,
                          border: '1px solid #ccc',
                          background: '#fff',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          style={{
                            height: 220,
                            background: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            position: 'relative',
                          }}
                          onClick={() => {
                            // Open in new tab for better viewing
                            window.open(doc.url, '_blank');
                          }}
                          title="Click to open in new tab"
                        >
                          {isImage ? (
                            <img 
                              src={doc.url} 
                              alt={doc.original_name} 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                              onError={(e) => {
                                // Handle broken images - show fallback
                                const target = e.currentTarget;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = `
                                    <div style="text-align: center; color: #999; width: 100%; padding: 20px;">
                                      <div style="font-size: 48px; margin-bottom: 8px;">📷</div>
                                      <div style="font-size: 12px; font-weight: 600; margin-bottom: 4px;">Image preview unavailable</div>
                                      <div style="font-size: 10px; color: #666;">Click to open</div>
                                    </div>
                                  `;
                                }
                              }}
                            />
                          ) : isPdf ? (
                            <div style={{ textAlign: 'center', padding: '20px', width: '100%' }}>
                              <div style={{ fontSize: 48, marginBottom: 8, color: '#d32f2f' }}>📄</div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4 }}>PDF Document</div>
                              <div style={{ fontSize: 10, color: '#666' }}>Click to open</div>
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', width: '100%', padding: '20px' }}>
                              <div style={{ fontSize: 32, fontWeight: 700, color: '#555', marginBottom: 8 }}>{ext}</div>
                              <div style={{ fontSize: 10, color: '#666' }}>Click to open</div>
                            </div>
                          )}
                        </div>
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '6px 8px',
                            fontSize: 11,
                            color: '#204a9a',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                          title={doc.original_name}
                        >
                          {doc.original_name}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => handleEditAssignment(selectedAssignment)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                  background: '#3e3e3e',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Edit assignment
              </button>
              <button
                type="button"
                onClick={() => handleDeleteExistingAssignment(selectedAssignment)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 4,
                  border: 'none',
                  cursor: 'pointer',
                  background: '#a33',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Delete assignment
              </button>
            </div>
          </div>
        </>
      );
    }

    const isPastDue = (due: string) => {
      const date = new Date(due);
      if (Number.isNaN(date.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      return date < today;
    };

    // Group assignments by form level with a safe fallback label.
    const groupAssignmentsByForm = (assignmentList: TeacherAssignment[]) => {
      const grouped: Record<string, { label: string; sortOrder: number; assignments: TeacherAssignment[] }> = {};
      assignmentList.forEach((assignment) => {
        const formLevel = assignment.classLevel ?? inferClassLevel(assignment.className);
        const fallbackClassLabel = assignment.className?.trim() || 'Unassigned Class';
        const groupLabel = formLevel ? formatClassLevelLabel(currentSchool, formLevel) : fallbackClassLabel;
        const groupKey = formLevel ? `form-${formLevel}` : `class-${fallbackClassLabel.toLowerCase()}`;

        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            label: groupLabel,
            sortOrder: formLevel ?? Number.MAX_SAFE_INTEGER,
            assignments: [],
          };
        }
        grouped[groupKey].assignments.push(assignment);
      });
      return grouped;
    };

    const upcomingAssignments = assignments.filter((a) => !isPastDue(a.dueDate));
    const readyToGradeAssignments = assignments.filter((a) => isPastDue(a.dueDate));

    const upcomingByForm = groupAssignmentsByForm(upcomingAssignments);
    const toGradeByForm = groupAssignmentsByForm(readyToGradeAssignments);

    const toggleFormExpanded = (formKey: string) => {
      setExpandedForms((prev) => ({
        ...prev,
        [formKey]: !prev[formKey],
      }));
    };

    const renderFormGroup = (
      formLabel: string,
      formGroupKey: string,
      formAssignments: TeacherAssignment[],
      sectionType: 'pending' | 'toGrade'
    ) => {
      const formKey = `${sectionType}-${formGroupKey}`;
      const isExpanded = expandedForms[formKey] !== false; // Default to expanded
      const sortedAssignments = [...formAssignments].sort((a, b) => {
        const dateA = new Date(a.dueDate).getTime();
        const dateB = new Date(b.dueDate).getTime();
        return dateA - dateB;
      });

      return (
        <div key={formKey} style={{ marginBottom: 16 }}>
          <div
            onClick={() => toggleFormExpanded(formKey)}
            style={{
              background: '#5a5a5a',
              color: '#fff',
              padding: '14px 18px',
              fontWeight: 600,
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              userSelect: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#4a4a4a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#5a5a5a';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 18 }}>{formLabel}</span>
              <span
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {formAssignments.length} {formAssignments.length === 1 ? 'assignment' : 'assignments'}
              </span>
            </div>
            <span
              style={{
                fontSize: 18,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            >
              ▼
            </span>
          </div>

          {isExpanded && (
            <div style={{ marginTop: 8, paddingLeft: 4 }}>
              {sortedAssignments.map((assignment) => (
                <div
                  key={assignment.id}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 6,
                    padding: 16,
                    marginBottom: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    transition: 'box-shadow 0.2s, transform 0.1s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: '#333',
                        }}
                      >
                        {assignment.subject}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#666',
                          background: '#f5f5f5',
                          padding: '2px 8px',
                          borderRadius: 4,
                        }}
                      >
                        Due: {formatDueDate(assignment.dueDate)}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 15,
                        color: '#555',
                        lineHeight: 1.5,
                        marginBottom: 4,
                      }}
                    >
                      {assignment.title}
                    </div>
                  </div>
                  <div style={{ marginLeft: 16 }}>
                    <button
                      type="button"
                      onClick={() =>
                        sectionType === 'pending'
                          ? viewAssignmentDetails(assignment)
                          : goToGradingForAssignment(assignment)
                      }
                      style={{
                        background: sectionType === 'pending' ? '#3e3e3e' : '#2d7a3d',
                        color: '#fff',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          sectionType === 'pending' ? '#2d2d2d' : '#226030';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          sectionType === 'pending' ? '#3e3e3e' : '#2d7a3d';
                      }}
                    >
                      {sectionType === 'pending' ? 'View Details' : 'Grade'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    const sortedGroups = (
      groups: Record<string, { label: string; sortOrder: number; assignments: TeacherAssignment[] }>
    ) =>
      Object.entries(groups).sort(([, a], [, b]) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.label.localeCompare(b.label);
      });

    return (
      <>
        <div className={styles.sectionHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 className={styles.sectionTitle}>📝 Assignments</h1>
            <p className={styles.sectionSubtitle}>Create and manage assignments for your students</p>
          </div>
          <button
            type="button"
            onClick={() => setAssignmentsView('create')}
            className={styles.primaryButton}
          >
            <span>➕</span>
            Create Assignment
          </button>
        </div>

        <div
          onClick={() => setPendingExpanded(!pendingExpanded)}
          className={styles.collapsibleHeader}
        >
          <div className={styles.collapsibleTitle}>
            <span>📋</span>
            Pending Assignments
            <span className={styles.collapsibleCount}>
              {upcomingAssignments.length}
            </span>
          </div>
          <span className={`${styles.collapsibleIcon} ${pendingExpanded ? styles.expanded : ''}`}>
            ▼
          </span>
        </div>

        {pendingExpanded && (
          <div style={{ marginBottom: 32 }}>
            {sortedGroups(upcomingByForm).length > 0 ? (
              sortedGroups(upcomingByForm).map(([groupKey, group]) =>
                renderFormGroup(group.label, groupKey, group.assignments, 'pending')
              )
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📭</div>
                <div className={styles.emptyStateTitle}>No pending assignments</div>
                <div className={styles.emptyStateDescription}>
                  Create a new assignment to get started
                </div>
              </div>
            )}
          </div>
        )}

        <div
          onClick={() => setToGradeExpanded(!toGradeExpanded)}
          className={styles.collapsibleHeader}
          style={{ marginTop: 24 }}
        >
          <div className={styles.collapsibleTitle}>
            <span>✏️</span>
            Assignments to Grade
            <span className={styles.collapsibleCount}>
              {readyToGradeAssignments.length}
            </span>
          </div>
          <span className={`${styles.collapsibleIcon} ${toGradeExpanded ? styles.expanded : ''}`}>
            ▼
          </span>
        </div>

        {toGradeExpanded && (
          <div>
            {sortedGroups(toGradeByForm).length > 0 ? (
              sortedGroups(toGradeByForm).map(([groupKey, group]) =>
                renderFormGroup(group.label, groupKey, group.assignments, 'toGrade')
              )
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>✨</div>
                <div className={styles.emptyStateTitle}>All caught up!</div>
                <div className={styles.emptyStateDescription}>
                  No assignments are ready for grading
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const gradingContent = () => {
    return <TeacherGradingSection />;
  };

  const timetableContent = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const { today } = getTodayAndTomorrow();

    return (
      <>
        <div className={styles.sectionHeader}>
          <h1 className={styles.sectionTitle}>📅 My Timetable</h1>
          <p className={styles.sectionSubtitle}>Your weekly teaching schedule</p>
        </div>

        {loadingTimetables ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            Loading timetable...
          </div>
        ) : timetables.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📭</div>
            <div className={styles.emptyStateTitle}>No timetable available</div>
            <div className={styles.emptyStateDescription}>
              Your timetable hasn't been assigned yet. Please contact the administrator.
            </div>
          </div>
        ) : (
          <div className={styles.timetableGrid}>
            {days.map((day) => {
              const dayTimetable = timetables
                .filter((t: TimetableDto) => t.day === day)
                .sort((a, b) => a.period - b.period);
              const isToday = day === today;

              return (
                <div key={day} className={styles.timetableDay}>
                  <div className={styles.timetableDayHeader}>
                    {isToday && <span style={{ marginRight: 8 }}>📍</span>}
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                    {isToday && (
                      <span style={{ 
                        marginLeft: 'auto', 
                        fontSize: 11, 
                        background: '#2563eb', 
                        color: '#fff', 
                        padding: '4px 12px', 
                        borderRadius: 20,
                        fontWeight: 600
                      }}>
                        TODAY
                      </span>
                    )}
                  </div>
                  <div className={styles.timetableClasses}>
                    {dayTimetable.length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                        No classes scheduled
                      </div>
                    ) : (
                      dayTimetable.map((entry) => (
                        <div key={entry.id} className={styles.timetableClass}>
                          <div className={styles.timetableTime}>
                            {formatTime(entry.start_time || null)}
                            {entry.end_time && (
                              <>
                                <br />
                                <span style={{ opacity: 0.6 }}>to {formatTime(entry.end_time || null)}</span>
                              </>
                            )}
                          </div>
                          <div className={styles.timetableDetails}>
                            <div className={styles.timetableSubject}>
                              {entry.subject?.name || 'No Subject'}
                            </div>
                            <div className={styles.timetableInfo}>
                              {formatClassLevelLabel(currentSchool, entry.form)} • Period {entry.period}
                            </div>
                          </div>
                          {entry.room && (
                            <div className={styles.timetableRoom}>
                              🏫 Room {entry.room}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const profileContent = () => (
    <div className={styles.profileSection}>
      <div className={styles.sectionHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1 className={styles.sectionTitle}>👤 My Profile</h1>
          {renderLearnMore('Learn more about My Profile', [
            'Update phone and recovery email, then click Save Changes.',
            'Click your avatar to upload or replace your profile picture.',
            'Review your assigned classes and subjects in the lower section.',
          ])}
        </div>
        <p className={styles.sectionSubtitle}>Manage your personal information and preferences</p>
      </div>

      {/* Profile Picture Section */}
      <div className={styles.avatarSection}>
        <div className={styles.avatarWrapper} onClick={handleAvatarClick}>
          {avatarPreview ? (
            <img src={avatarPreview} alt="Profile" className={styles.avatarPreview} />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {teacherName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className={styles.avatarOverlay}>
            <span style={{ color: '#fff', fontSize: 24 }}>📷</span>
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={handleAvatarChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />
        <div className={styles.avatarActions}>
          <div className={styles.avatarInfo}>
            <strong>{teacherName}</strong>
            <br />
            Click on the profile picture to change it
          </div>
          {pendingAvatarFile && (
            <button
              type="button"
              onClick={handleAvatarSave}
              className={styles.primaryButton}
              style={{ padding: '10px 20px', fontSize: 13 }}
            >
              💾 Save Profile Picture
            </button>
          )}
          {avatarPreview && !pendingAvatarFile && (
            <button
              type="button"
              onClick={handleRemoveAvatar}
              className={styles.dangerButton}
              style={{ padding: '10px 20px', fontSize: 13 }}
            >
              🗑️ Remove Picture
            </button>
          )}
        </div>
      </div>

      {/* Profile Information Fields */}
      <div className={styles.contentCard}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>📋</span>
            Personal Information
          </div>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Full Name</label>
            <div className={styles.formInput} style={{ background: '#f8fafc', cursor: 'not-allowed' }}>
              {teacherName}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Phone Number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter your phone number"
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Email Address</label>
            <input
              type="email"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              placeholder="your.email@example.com"
              className={styles.formInput}
            />
            <div className={styles.formHint}>
              Used for announcements and password recovery
            </div>
          </div>

          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className={styles.primaryButton}
          >
            {savingProfile ? '⏳ Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>

      {/* Classes & Forms Section */}
      <div className={styles.contentCard}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>📚</span>
            My Classes & Subjects
          </div>
        </div>
        <div className={styles.cardBody}>
          {teacherSubjects.length === 0 ? (
            <div className={styles.emptyState} style={{ padding: 32 }}>
              <div className={styles.emptyStateIcon}>📭</div>
              <div className={styles.emptyStateTitle}>No classes assigned</div>
              <div className={styles.emptyStateDescription}>
                You haven't been assigned to any classes yet. Contact an administrator.
              </div>
            </div>
          ) : (
            <div className={styles.itemList}>
              {teacherSubjects.map((ts) => (
                <div key={ts.id} className={styles.listItem} style={{ cursor: 'default' }}>
                  <div className={styles.listItemIcon}>📖</div>
                  <div className={styles.listItemContent}>
                    <div className={styles.listItemTitle}>
                      {ts.subject?.name || 'Unknown Subject'}
                    </div>
                    <div className={styles.listItemMeta}>
                      {formatClassLevelLabel(currentSchool, ts.form)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const loadTeacherClubs = async () => {
    setLoadingClubs(true);
    try {
      const clubs = await fetchActiveClubs({ school: currentSchool });
      if (!user) {
        setTeacherClubs([]);
        return;
      }
      const teacherId = user.id;
      const myClubs: ClubDto[] = [];
      for (const club of clubs) {
        try {
          const data = await fetchClubMembers(club.id);
          const isTeacherManager = data.members.some(
            (m) => m.id === teacherId && m.pivot?.role === 'teacher'
          );
          if (isTeacherManager) {
            myClubs.push(club);
          }
        } catch (error) {
          // Ignore per-club errors; continue
        }
      }
      setTeacherClubs(myClubs);
    } finally {
      setLoadingClubs(false);
    }
  };

  const openTeacherClubMembers = async (club: ClubDto) => {
    setSelectedClubForMembers(club);
    setLoadingClubMembers(true);
    try {
      const data = await fetchClubMembers(club.id);
      setClubMembers(data.members);
    } finally {
      setLoadingClubMembers(false);
    }
  };

  const refreshTeacherClubMembers = async () => {
    if (!selectedClubForMembers) return;
    setLoadingClubMembers(true);
    try {
      const data = await fetchClubMembers(selectedClubForMembers.id);
      setClubMembers(data.members);
    } finally {
      setLoadingClubMembers(false);
    }
  };

  const handleTeacherAddStudentToClub = async () => {
    if (!selectedClubForMembers || !selectedStudentIdForClub) return;
    try {
      await addClubMember(selectedClubForMembers.id, Number(selectedStudentIdForClub), 'student');
      setSelectedStudentIdForClub('');
      await refreshTeacherClubMembers();
    } catch (error: any) {
      console.error('Failed to add student to club', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to add student to club';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleTeacherRemoveClubMember = async (userId: number, name: string) => {
    if (!selectedClubForMembers) return;
    if (!window.confirm(`Remove ${name} from ${selectedClubForMembers.name}?`)) {
      return;
    }
    try {
      await removeClubMember(selectedClubForMembers.id, userId);
      await refreshTeacherClubMembers();
    } catch (error: any) {
      console.error('Failed to remove club member', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to remove club member';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleTeacherUpdateClubMemberRole = async (userId: number, newRole: string) => {
    if (!selectedClubForMembers) return;
    try {
      await updateClubMemberRole(selectedClubForMembers.id, userId, newRole);
      await refreshTeacherClubMembers();
    } catch (error: any) {
      console.error('Failed to update club member role', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to update club member role';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const clubsContent = () => (
    <div className={styles.sectionWrapper}>
      <div className={styles.sectionHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1 className={styles.sectionTitle}>My Clubs 🏆</h1>
          {renderLearnMore('Learn more about My Clubs', [
            'Use Refresh My Clubs to load the latest memberships.',
            'Open Manage Members to add students and assign roles.',
            'Keep leadership roles accurate so club records stay organized.',
          ])}
        </div>
        <p className={styles.sectionSubtitle}>
          Manage membership for clubs where you are registered as a teacher manager.
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <button
          className={styles.primaryButton}
          onClick={loadTeacherClubs}
          disabled={loadingClubs}
        >
          {loadingClubs ? 'Loading clubs...' : 'Refresh My Clubs'}
        </button>
      </div>

      {teacherClubs.length === 0 && !loadingClubs ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>🏆</div>
          <div className={styles.emptyStateTitle}>No managed clubs</div>
          <div className={styles.emptyStateDescription}>
            You will see clubs here once an administrator adds you as a teacher to a club.
          </div>
        </div>
      ) : (
        <div className={styles.itemList}>
          {teacherClubs.map((club) => (
            <div key={club.id} className={styles.listItem}>
              <div className={styles.listItemIcon}>🏆</div>
              <div className={styles.listItemContent}>
                <div className={styles.listItemTitle}>{club.name}</div>
                <div className={styles.listItemMeta}>
                  <span>{club.description || 'No description'}</span>
                  <span>Status: {club.status}</span>
                </div>
              </div>
              <button
                className={styles.primaryButton}
                onClick={() => openTeacherClubMembers(club)}
              >
                Manage Members
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedClubForMembers && (
        <div style={{ marginTop: 32 }}>
          <h2 className={styles.sectionTitle} style={{ fontSize: 20 }}>
            Members – {selectedClubForMembers.name}
          </h2>

          <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={selectedStudentIdForClub}
              onChange={(e) => setSelectedStudentIdForClub(e.target.value ? Number(e.target.value) : '')}
              style={{
                flex: 1,
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                fontSize: 14,
              }}
            >
              <option value="">Add student...</option>
              {assignedClasses
                .flatMap((ac) => ac.students || [])
                .map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name}
                  </option>
                ))}
            </select>
            <button
              className={styles.primaryButton}
              onClick={handleTeacherAddStudentToClub}
              disabled={loadingClubMembers}
            >
              Add Student
            </button>
          </div>

          {loadingClubMembers ? (
            <div className={styles.loading}>
              <div className={styles.loadingSpinner}></div>
              Loading members...
            </div>
          ) : clubMembers.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>👥</div>
              <div className={styles.emptyStateTitle}>No members yet</div>
              <div className={styles.emptyStateDescription}>
                Use the control above to add students to this club.
              </div>
            </div>
          ) : (
            <div className={styles.itemList}>
              {clubMembers.map((member) => (
                <div key={member.id} className={styles.listItem}>
                  <div className={styles.listItemIcon}>👤</div>
                  <div className={styles.listItemContent}>
                    <div className={styles.listItemTitle}>{member.name}</div>
                    <div className={styles.listItemMeta}>
                      <span>{member.email}</span>
                      {member.form && <span>{formatClassLevelLabel(currentSchool, member.form)}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                      value={member.pivot?.role || 'member'}
                      onChange={(e) => handleTeacherUpdateClubMemberRole(member.id, e.target.value)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        fontSize: 12,
                      }}
                    >
                      <option value="teacher">Teacher</option>
                      <option value="student">Student</option>
                      <option value="president">President</option>
                      <option value="vice_president">Vice President</option>
                      <option value="secretary">Secretary</option>
                      <option value="member">Member</option>
                    </select>
                    <button
                      className={styles.secondaryButton}
                      onClick={() => handleTeacherRemoveClubMember(member.id, member.name)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                setSelectedClubForMembers(null);
                setClubMembers([]);
                setSelectedStudentIdForClub('');
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const announcementsContent = () => (
    <>
      <div className={styles.sectionHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className={styles.sectionTitle}>📢 Announcements</h1>
            {renderLearnMore('Learn more about Announcements', [
              'Create Class Announcement to send targeted updates to your classes.',
              'Create Club Announcement to send updates to any club in your school.',
              'Use My Class Announcements to review and clean up your own posts.',
              'Use Club Announcements to edit or delete club updates.',
              'Check School Announcements for administration-wide notices.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>View school announcements and create class or club announcements</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setShowAnnouncementModal(true)}
            className={styles.primaryButton}
          >
            <span>➕</span>
            Create Class Announcement
          </button>
          <button
            type="button"
            onClick={openCreateClubAnnouncementModal}
            className={styles.secondaryButton}
          >
            <span>🏆</span>
            Create Club Announcement
          </button>
        </div>
      </div>

      {/* My Class Announcements Section */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ margin: '0 0 16px', color: '#1e3a5f' }}>📨 My Class Announcements</h3>

        {loadingClassAnnouncements ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            Loading class announcements...
          </div>
        ) : classAnnouncements.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📭</div>
            <div className={styles.emptyStateTitle}>No class announcements yet</div>
            <div className={styles.emptyStateDescription}>
              Create your first class announcement to notify your students
            </div>
          </div>
        ) : (
          <div className={styles.itemList}>
            {classAnnouncements.map((announcement) => (
              <div key={announcement.id} className={styles.announcementCard}>
                <div className={styles.announcementHeader}>
                  <div className={styles.announcementIcon}>
                    {announcement.email_sent ? '📧' : '📝'}
                  </div>
                  <div className={styles.announcementContent}>
                    <div className={styles.announcementTitle}>{announcement.title}</div>
                    <div className={styles.announcementTime}>
                      🕒 {formatTimeAgo(announcement.created_at)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span 
                      className={styles.announcementBadge}
                      style={{
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#4f46e5',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                      }}
                    >
                      {formatClassLevelLabel(currentSchool, announcement.form)}
                    </span>
                    {announcement.email_sent && (
                      <span
                        className={styles.announcementBadge}
                        style={{
                          background: 'rgba(16, 185, 129, 0.1)',
                          color: '#059669',
                          border: '1px solid rgba(16, 185, 129, 0.2)',
                        }}
                      >
                        Email Sent
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteClassAnnouncement(announcement.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#e53e3e',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        fontSize: 18,
                        borderRadius: 4,
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(229, 62, 62, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                      title="Delete announcement"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <div className={styles.announcementMessage}>
                  {announcement.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Club Announcements Section */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ margin: '0 0 16px', color: '#1e3a5f' }}>🏆 Club Announcements</h3>
        {loadingClubAnnouncements ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            Loading club announcements...
          </div>
        ) : clubAnnouncements.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📭</div>
            <div className={styles.emptyStateTitle}>No club announcements yet</div>
            <div className={styles.emptyStateDescription}>
              Create a club announcement to notify members.
            </div>
          </div>
        ) : (
          <div className={styles.itemList}>
            {clubAnnouncements.map((announcement) => (
              <div key={announcement.id} className={styles.announcementCard}>
                <div className={styles.announcementHeader}>
                  <div className={styles.announcementIcon}>🏆</div>
                  <div className={styles.announcementContent}>
                    <div className={styles.announcementTitle}>{announcement.title}</div>
                    <div className={styles.announcementTime}>
                      🕒 {formatTimeAgo(announcement.created_at)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className={styles.announcementBadge}>
                      {announcement.club?.name || `Club #${announcement.club_id}`}
                    </span>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={() => openEditClubAnnouncementModal(announcement)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.dangerButton}
                      onClick={() => handleDeleteClubAnnouncement(announcement.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className={styles.announcementMessage}>
                  {announcement.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* School-wide Announcements Section */}
      <div>
        <h3 style={{ margin: '0 0 16px', color: '#1e3a5f' }}>📣 School Announcements</h3>

        {loadingAnnouncements ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            Loading school announcements...
          </div>
        ) : announcements.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📭</div>
            <div className={styles.emptyStateTitle}>No school announcements</div>
            <div className={styles.emptyStateDescription}>
              There are no school-wide announcements at this time
            </div>
          </div>
        ) : (
          <div className={styles.itemList}>
            {announcements.map((announcement) => (
              <div key={announcement.id} className={styles.announcementCard}>
                <div className={styles.announcementHeader}>
                  <div className={styles.announcementIcon}>
                    {announcement.target_group === 'all' ? '📣' : '📌'}
                  </div>
                  <div className={styles.announcementContent}>
                    <div className={styles.announcementTitle}>{announcement.title}</div>
                    <div className={styles.announcementTime}>
                      🕒 {formatTimeAgo(announcement.created_at)}
                    </div>
                  </div>
                  <span 
                    className={styles.announcementBadge}
                    style={{
                      background: announcement.target_group === 'all' 
                        ? 'rgba(16, 185, 129, 0.1)' 
                        : 'rgba(59, 130, 246, 0.1)',
                      color: announcement.target_group === 'all' 
                        ? '#059669' 
                        : '#2563eb',
                      border: `1px solid ${announcement.target_group === 'all' 
                        ? 'rgba(16, 185, 129, 0.2)' 
                        : 'rgba(59, 130, 246, 0.2)'}`,
                    }}
                  >
                    {announcement.target_group === 'all' ? 'Everyone' : 'Teachers'}
                  </span>
                </div>
                <div className={styles.announcementMessage}>
                  {announcement.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const renderMain = () => {
    switch (activeSection) {
      case 'assignments':
        return assignmentsContent();
      case 'grading':
        return gradingContent();
      case 'timetable':
        return timetableContent();
      case 'announcements':
        return announcementsContent();
      case 'clubs':
        return clubsContent();
      case 'profile':
        return profileContent();
      case 'dashboard':
      default:
        return (
          <DashboardView
            teacherName={teacherName}
            assignments={assignments}
            teacherSubjects={teacherSubjects}
            announcements={announcements}
            timetables={timetables}
            loadingAnnouncements={loadingAnnouncements}
            loadingTimetables={loadingTimetables}
            onNavigate={(section) => setActiveSection(section as Section)}
            school={currentSchool}
          />
        );
    }
  };

  const navItems = [
    { key: 'dashboard' as Section, label: 'Dashboard', icon: '🏠' },
    { key: 'assignments' as Section, label: 'Assignments', icon: '📝' },
    { key: 'grading' as Section, label: 'Grading', icon: '✏️' },
    { key: 'timetable' as Section, label: 'Timetable', icon: '📅' },
    { key: 'announcements' as Section, label: 'Announcements', icon: '📢' },
    { key: 'clubs' as Section, label: 'My Clubs', icon: '🏆' },
  ];

  const getSectionTitle = () => {
    switch (activeSection) {
      case 'dashboard': return 'Dashboard';
      case 'assignments': return 'Assignments';
      case 'grading': return 'Grading';
      case 'timetable': return 'Timetable';
      case 'announcements': return 'Announcements';
      case 'clubs': return 'My Clubs';
      case 'profile': return 'My Profile';
      default: return 'Dashboard';
    }
  };

  return (
    <div className={styles.dashboardContainer} data-school={currentSchool === 'TLC' ? 'tlc' : 'kba'}>
      {/* Mobile Menu Toggle */}
      {isMobileView && (
        <button 
          className={styles.mobileMenuToggle}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          ☰
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.show : ''}`}
          onClick={closeMobileMenu}
          aria-label="Close menu"
        />
      )}

      {isSwitchingSchool && <LoadingOverlay label="Switching school..." />}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${isMobileMenuOpen ? styles.mobileOpen : ''}`}>
        {/* Logo Header */}
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarLogo}>
            <div className={styles.logoIcon}>
              <img 
                src={currentSchool === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg'} 
                alt={`${currentSchool} Logo`} 
                style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
              />
            </div>
            <div className={styles.logoText}>
              <div className={styles.sidebarTitle}>{schoolInfo.shortName}</div>
              <div className={styles.sidebarSubtitle}>Teacher Portal</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className={styles.navSection}>
          <div className={styles.navLabel}>Main Menu</div>
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`${styles.navButton} ${activeSection === item.key ? styles.active : ''}`}
              onClick={() => {
                setActiveSection(item.key);
                if (item.key === 'assignments') {
                  setAssignmentsView('main');
                  setSelectedAssignment(null);
                }
                if (item.key === 'announcements') {
                  loadAnnouncements();
                  loadClassAnnouncements();
                  loadClubAnnouncements();
                }
                if (item.key === 'timetable') {
                  loadTimetables();
                }
                closeMobileMenu();
              }}
            >
              <span className={styles.navIcon}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className={styles.sidebarProfile}>
          <button
            type="button"
            className={styles.profileButton}
            onClick={() => {
              setActiveSection('profile');
              closeMobileMenu();
            }}
          >
            <div className={styles.profileAvatar}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Profile" />
              ) : (
                teacherName.charAt(0).toUpperCase()
              )}
            </div>
            <div className={styles.profileInfo}>
              <div className={styles.profileName}>{teacherName}</div>
              <div className={styles.profileRole}>Teacher</div>
            </div>
          </button>
          <button
            type="button"
            className={styles.logoutButton}
            onClick={() => {
              closeMobileMenu();
              setShowLogoutConfirmation(true);
            }}
          >
            <span>🚪</span>
            Sign Out
          </button>
          <div className={styles.sidebarAttribution}>POWERED BY BIZNARY TECH SOLUTIONS</div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Top Bar */}
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <div className={styles.breadcrumb}>
              Teacher Portal / <span>{getSectionTitle()}</span>
            </div>
          </div>
          <div className={styles.topBarRight}>
            <button 
              className={styles.topBarButton} 
              title="Announcements"
              onClick={() => {
                setActiveSection('announcements');
                loadAnnouncements();
                loadClassAnnouncements();
                loadClubAnnouncements();
              }}
            >
              🔔
              {newAnnouncementCount > 0 && (
                <span className={styles.topBarNotificationBadge}>{bellBadgeCount}</span>
              )}
            </button>
          </div>
        </header>

        {/* Content Wrapper */}
        <div className={styles.contentWrapper}>
          <div className={styles.contentInner}>
            {renderMain()}
          </div>
        </div>
      </div>

      {/* Class Announcement Modal */}
      <ClassAnnouncementModal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        onSubmit={handleCreateClassAnnouncement}
        assignedClasses={assignedClasses}
      />

      {showClubAnnouncementModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 16,
          }}
          onClick={() => setShowClubAnnouncementModal(false)}
        >
          <div
            className={styles.contentCard}
            style={{ width: '100%', maxWidth: 620, margin: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <span className={styles.cardTitleIcon}>🏆</span>
                {editingClubAnnouncement ? 'Edit Club Announcement' : 'Create Club Announcement'}
              </div>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Club</label>
                <select
                  className={styles.formInput}
                  value={clubAnnouncementForm.clubId}
                  onChange={(e) =>
                    setClubAnnouncementForm((prev) => ({ ...prev, clubId: Number(e.target.value) || 0 }))
                  }
                >
                  <option value={0}>Select club</option>
                  {availableAnnouncementClubs.map((club) => (
                    <option key={club.id} value={club.id}>
                      {club.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Title</label>
                <input
                  className={styles.formInput}
                  value={clubAnnouncementForm.title}
                  onChange={(e) => setClubAnnouncementForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Announcement title"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Message</label>
                <textarea
                  className={styles.formInput}
                  rows={6}
                  value={clubAnnouncementForm.message}
                  onChange={(e) => setClubAnnouncementForm((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder="Write your announcement message"
                />
              </div>
              {!editingClubAnnouncement && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <input
                    type="checkbox"
                    checked={clubAnnouncementForm.sendEmail}
                    onChange={(e) =>
                      setClubAnnouncementForm((prev) => ({ ...prev, sendEmail: e.target.checked }))
                    }
                  />
                  Send as email to club members
                </label>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => setShowClubAnnouncementModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={handleSaveClubAnnouncement}
                >
                  {editingClubAnnouncement ? 'Save Changes' : 'Create Announcement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutConfirmation}
        onConfirm={() => {
          setShowLogoutConfirmation(false);
          logout();
        }}
        onCancel={() => setShowLogoutConfirmation(false)}
      />
    </div>
  );
};

